/**
 * Port-extension display settings — visual effects the original never drew, so
 * a player who wants the 1990 picture can turn them off.
 *
 * Today that is seven plain toggles — `PROPELLER EFFECT`
 * (AD-012 §6), `KILL CAMERA` (AD-015), `ENHANCED EFFECTS`
 * (`particle-effects.ts`), `ENHANCED MODELS`
 * (`docs/engineering/enhanced-models.md`), `ENHANCED TERRAIN`
 * (`three/terrain-decoration.ts`), `ENHANCED SKY` (`enhanced-sky.ts`) and
 * `3D COCKPIT` (`docs/engineering/cockpit-3d-design.md`). All
 * are deliberately
 * **not** on the Realism Panel: that panel is a pixel-exact reproduction of the
 * original's own screen (`docs/game/ui-menus.md`), every rect on it is a
 * recovered option, and there is no spare rect for a setting the original had
 * no concept of. `packages/engine`'s `RealismSettings` excludes them for the
 * same reason `cockpit-settings.ts` is excluded — `flight/tiers.ts`:
 * presentation belongs to the UI. So they live on the port's own PREFERENCES
 * panel (`shell/model/preferences-screen.ts`), in its DISPLAY section, and
 * their labels say what they are. Until this wave they were seven chips in a
 * corner of the flight view; the panel is what replaced that row, which is
 * also what stopped the row being at capacity (`app.css`).
 *
 * **All seven are on by default** — which is
 * the opposite of `cockpit-settings.ts`'s
 * choice and for a compatible reason: that toggle defaults off because turning
 * it on *hides* things callers already see, whereas these add something nothing
 * depends on, and the user asked for each of them as it landed (2026-08-21 for
 * the first three, 2026-08-23 for `ENHANCED EFFECTS` and `ENHANCED MODELS`,
 * 2026-08-26 for `ENHANCED TERRAIN`, `ENHANCED SKY` and `3D COCKPIT`, which had
 * shipped default-off while they settled and are now judged settled enough to
 * join the rest). `ENHANCED SKY`'s own migration has one more wrinkle worth
 * knowing before touching it — see `storedEnhancedSky`. Persisted the same way
 * — a small `SettingsStore` round-trip, storage-agnostic so it is testable
 * without a real `localStorage`.
 */

import type { SettingsStore } from "../audio/settings.ts";

export const EFFECTS_SETTINGS_STORAGE_KEY = "red-opus/effects-settings";

/**
 * The pictures of the sky this port can draw. **Two of them are settings and the
 * third is a measuring stick.**
 *
 * The time of day is *mission data* — `MissionWeather.timeOfDay`, briefed by the
 * generator, printed on the briefing screen — so it always drives the sky. What
 * a player chooses is the **treatment**, and there are two:
 *
 * - **`original`** — the game's own machinery, hour-driven: `setSkyShades`'
 *   16-entry banded run for the hour, the sunset/twilight ramps,
 *   `STARS.DAT`'s 31 authentic points at night, `SUN.BMP`'s flat disc
 *   (`time-of-day.ts`), and cloud in the slab's own baked flat 79.
 * - **`enhanced`** — the same briefing, painted properly: a three-stop
 *   hemisphere, the sun's own glare in high dynamic range, the ephemeris night
 *   sky with a phase-correct moon (`night-sky.ts`), and ramp-shaded cloud
 *   (`enhanced-sky.ts`). **It changes the sky and nothing else** — the ground it
 *   paints is byte-identical to `original`'s, which is the property that stops a
 *   toggle named SKY reading as a ground control (user report, 2026-08-23: "I
 *   primarily see color change on the ground").
 *
 * `measured` is the third, and it is **not a user-facing setting**: one flat
 * `#7db6c3` sky over one flat `#4d5928` ground whatever the mission briefs, plus
 * the binary's own constant `#bababa` overcast deck at every hour. It is the
 * only sky anybody has ever *measured* out of the original
 * (`world-terrain.md` §7.1, a daytime clear-sky sortie), which makes it a real
 * byte-comparison reference and worth keeping reachable —
 * `/fly?treatment=measured`, via `skyTreatmentOverride`. It stopped being a
 * *choice* because offering it as one asked a player to turn the briefed hour
 * off, and an hour is not a port extension: it is what the mission says.
 */
export const SKY_TREATMENTS = ["measured", "original", "enhanced"] as const;
export type SkyTreatment = (typeof SKY_TREATMENTS)[number];

/**
 * Which picture a sortie is drawn with: the setting, unless a debug override
 * names one. `override` is `skyTreatmentOverride`'s reading of the URL and is
 * `undefined` in every ordinary flight.
 */
export function skyTreatmentFor(settings: EffectsSettings, override?: SkyTreatment | undefined): SkyTreatment {
  return override ?? (settings.enhancedSky ? "enhanced" : "original");
}

/**
 * `/fly?treatment=<name>`'s query key.
 *
 * **Not `sky`**, which `mission-launch.ts` already owns as the mission's briefed
 * *weather* word (`?sky=overcast`). Sharing the key would have been read-only
 * safe — no `SkyCondition` is a `SkyTreatment` and vice versa, so neither parse
 * can produce a false positive — but it would have made `measured` **over an
 * overcast mission unreachable**, and that combination is precisely the one this
 * hatch exists to check: it is where the reference's constant `#bababa` deck
 * lives (`overcast-sky.ts`). One key, one meaning.
 */
export const SKY_TREATMENT_PARAM = "treatment";

/**
 * `?treatment=<name>` on `/fly` — the one way `measured` is still reachable, and
 * a way to pin either of the other two without touching a player's stored
 * settings.
 *
 * A debug hatch rather than a feature: nothing links to it, it is not persisted,
 * and an unrecognised value is `undefined` rather than an error, so a stray
 * query string cannot stop a flight from booting.
 */
export function skyTreatmentOverride(value: unknown): SkyTreatment | undefined {
  return SKY_TREATMENTS.find((treatment) => treatment === value);
}

export interface EffectsSettings {
  /** AD-012's translucent propeller disc on every aircraft in every view. */
  readonly propellerEffect: boolean;
  /** AD-015's corner window following an enemy down to its crash (`killcam.ts`). */
  readonly killCamera: boolean;
  /**
   * Draw the sky as `enhanced-sky.ts`'s modern dome rather than as the
   * original's own banded run — `SKY_TREATMENTS`' two user-facing pictures, as
   * the boolean they always were underneath.
   *
   * **The mission's hour drives the sky either way.** That is the whole point of
   * this being a boolean: `timeOfDay` is briefed mission data, so both
   * treatments are hour-driven and this only picks *how* the hour is painted.
   * There is nothing here to turn a night sortie back into a noon one.
   *
   * It does **not** gate the sun or the moon: `SUN.BMP` is the original's own
   * art, `init.c` asserts on loading it, and `sun.c` draws it regardless of any
   * option — so the disc is not a port extension and has nothing to switch off.
   * Nor does it gate the mission's **clouds**: those are `clouds.c`'s own
   * feature and ride REALISTIC WEATHER on the Realism Panel
   * (`sky-clouds.ts`). What it does to a cloud is *paint* it — three flat tones
   * off a `BARON.PAL` ramp on, the slab's own baked flat 79 `#929292` off.
   *
   * Nor, since this wave, does it gate AD-017's night searchlights. Those are
   * lit by the hour alone (`scene-searchlights.ts`'s `searchlightsLit`), which
   * is what they always meant to say: the old `!== "measured"` test existed
   * because `measured` drew a night mission as a blue noon sky and a beam in
   * one would have been absurd. With `measured` off the settings screen, both
   * treatments draw a night as a night and both get the beams.
   */
  readonly enhancedSky: boolean;
  /**
   * Fire, smoke and hit sparks on a burning or freshly-hit **aeroplane**, drawn
   * as particles instead of the original's held `EXPLO.BMP` billboard and
   * dithered `SMOKE.BMP` puffs (`particle-effects.ts`) — and, with it, a
   * **working train's** coal plume, drawn as particles instead of the sprite
   * path's thin `SMOKE.BMP` smudge.
   *
   * It **replaces** rather than adds: with this on, the sprite path is told to
   * skip aeroplanes *and* the train plume (`frame-signals.ts`'s
   * `FrameSourceOptions`) so nothing carries both. Off, every pixel is exactly
   * what it was before this toggle existed — which is the whole point of the
   * toggle and what `frame-signals.test.ts` pins.
   *
   * Its scope stops there *as a replacement*. A gasbag's fire, a wreck's ground
   * plume, a burning train's dying sequence and a flak burst stay sprite in both
   * states: those are the original's own art playing its own sequences, and
   * nobody asked for them to be re-imagined.
   *
   * It does carry two things that replace nothing, because there was nothing
   * there. The first is **B1's ground shadows** (`scene-shadows.ts`). The
   * original draws no shadow under an aeroplane in flight — `ground-shadow.ts`
   * has the pixel counts that settled it — so a shadow is a port extension
   * needing an existing home, and this is the row that already means "phenomena
   * around an aeroplane the original did not produce". Off, a take-off looks
   * exactly as it did before the feature existed.
   *
   * The second is the **dive whine** (`audio/dive-whine.ts`), and it is the one
   * thing on this panel that is not a picture. The original plays nothing off
   * airspeed but its `SIMSNDS-25` wind bed; a whine is a **declared invention**
   * the user asked for anyway (2026-08-26), on the same standing AD-018's
   * burning buildings and AD-020's building collisions have. It lands here
   * rather than on a row of its own because this row already means "the thing
   * the original did not do, and you may switch it off" — and because a
   * one-member DISPLAY row named SOUND would be a control most players would
   * never find a reason to touch. Off, the flight sounds exactly as it did
   * before the feature existed: `dive-whine.test.ts` and `engine.test.ts` both
   * pin that as **bit-zero samples**, not merely a low gain.
   */
  readonly enhancedEffects: boolean;
  /**
   * Draw an aeroplane from the offline-built enhanced `.glb`
   * (`docs/engineering/enhanced-models.md`) where one exists, and from its baked
   * `.TBL` where one does not — **per airframe**, so a sky holding one enhanced
   * type and three authentic ones is the ordinary case rather than a fault.
   *
   * Like `enhancedEffects` this *replaces* rather than adds, so its title says
   * what the off state looks like. Off, every aeroplane is exactly the geometry
   * the bake produced, which is the whole point of having the switch.
   *
   * On by default, which is what the user asked for (2026-08-23) and costs
   * nothing on an install that never ran the tool: with no
   * `/enhanced/manifest.json` the index is empty, every airframe takes the baked
   * path, and the switch does nothing at all.
   *
   * **It is also the resolution switch** (user, 2026-08-25: "when enhanced 3d is
   * enabled, let's up the resolution to native too for the 3d rendering
   * window"). On, a 3D surface draws at the display's own `devicePixelRatio`;
   * off, at `three/view-pixel-ratio.ts`'s cap of 2. There is no separate
   * RESOLUTION row for the reason there is no separate anything on this panel:
   * DISPLAY is a list of *pictures*, this is the one row in it whose name says
   * "the 3D is the good one", and a second control that only does something on a
   * display above 2x would be a row most players could never see move. The
   * coupling is stated in both directions — here and in `view-pixel-ratio.ts` —
   * because it is the one thing about this toggle that is not about geometry.
   */
  readonly enhancedModels: boolean;
  /**
   * Decoration drawn *on* the terrain: instanced trees in the copses the
   * original places as three spheres, and woods inside the landcover polygons
   * it paints in the same greens (`three/terrain-decoration.ts`).
   *
   * **It does not gate the terrain itself.** The rolling ground
   * (`packages/engines flight/terrain-relief.ts`) feeds the sim's `GroundQuery`, so it has to be
   * identical for every peer, every replay and every census; it is derived from
   * the region and is always on. This toggle only covers things nothing can
   * collide with.
   *
   * On by default (2026-08-26): it shipped off while it was the least settled
   * of the seven and changed the look of the whole map rather than of one
   * effect, and is now judged settled enough to join the rest on. Off is
   * still today's pixels exactly, for the player who wants them.
   */
  readonly enhancedTerrain: boolean;
  /**
   * **3D COCKPIT** — draw the player's own aeroplane from the
   * pilot's eye instead of painting the airframe around him
   * (`docs/engineering/cockpit-3d-design.md`).
   *
   * The original's cockpit is a *draw program*, not a bitmap
   * (`packages/baker/src/cockpit-bake.ts`), and it splits along the original's
   * own block boundaries into two families: the dashboard, the gun breeches and
   * the sight, which stay; and the coaming, cabane struts and rigging, which
   * are exactly what a real model of the aeroplane already has
   * (`cockpit-airframe.ts`). On, the airframe family is not drawn, the own-
   * aircraft mesh is no longer hidden in cockpit mode, and the dashboard is
   * mapped onto a quad in front of the eye so it turns with the aeroplane and
   * leaves the frame when the pilot looks over his shoulder.
   *
   * **It requires ENHANCED MODELS**, and the requirement is the feature rather
   * than a limitation: the baked `.TBL` mesh has no pilot to hide, no part
   * annotations and no cockpit opening, so "the real model" is a promise only
   * the `.glb` can keep. With ENHANCED MODELS off — or with no models being
   * served at all — the row says `UNAVAILABLE` the way `EffectsToggles.tsx`'s
   * own switch does, rather than silently drawing the wrong thing.
   *
   * **Phase 2 flies all 22 flyable scouts** (`COCKPIT_3D_AIRCRAFT`, derived
   * from the block-span table itself). An airframe with no cockpit art — every
   * AI-only two-seater and bomber — classifies nothing as structure and keeps
   * the painted treatment, which is the same inert answer the row's
   * `UNAVAILABLE` state gives.
   *
   * Shipped off (2026-08-25) while phase 2 flew the fleet out; on by default
   * as of 2026-08-26, now that all 22 flyable scouts render from the seat and
   * the row is judged settled rather than experimental. Off is still today's
   * pixels exactly, for the player who wants them — and still the answer for
   * any airframe classifying nothing as structure, or with ENHANCED MODELS off.
   */
  readonly cockpit3d: boolean;
}

export const DEFAULT_EFFECTS_SETTINGS: EffectsSettings = {
  propellerEffect: true,
  killCamera: true,
  enhancedEffects: true,
  enhancedModels: true,
  enhancedTerrain: true,
  enhancedSky: true,
  cockpit3d: true,
};

/**
 * What a settings object from **any** of this key's three eras means today.
 *
 * The eras, in order:
 *
 * 1. Two booleans, `timeOfDaySky` and `enhancedSky`. Never independent — an
 *    enhanced dome is time-of-day-coloured by construction — so the pair had
 *    four combinations naming three pictures.
 * 2. One three-state string, `sky`, cycling `measured` → `timeOfDay` →
 *    `enhanced`. Era 1 migrated into it here, and that migration is the pattern
 *    this one follows.
 * 3. One boolean again, `enhancedSky`, because the middle state was never a
 *    treatment: it was the *hour*, and the hour is mission data that no setting
 *    should be able to switch off (user, 2026-08-24).
 *
 * The round trip lands where it started, which is the pleasant part: era 1's
 * `enhancedSky` **is** era 3's, bit for bit. `timeOfDaySky` was the key that
 * carried the thing which turned out not to be a setting at all, and dropping
 * it is the whole of the collapse.
 *
 * Era 2 folds in lossless in the direction that matters — `enhanced` stays
 * enhanced, and both `measured` and `timeOfDay` land on `original`, which is the
 * only honest place for them: `original` is *what `timeOfDay` already drew*, and
 * a player who had parked the chip on `measured` was asking for the original's
 * picture too, just with the briefing thrown away as well. Nobody's sky gets
 * *more* added to it than they asked for, which is the direction that would have
 * been a surprise.
 *
 * **2026-08-26: the fallback direction flipped.** `ENHANCED SKY` joined the
 * rest of the row on by default (`DEFAULT_EFFECTS_SETTINGS`), so era 1 and era
 * 3's shared bit now reads `!== false` rather than `=== true` — an *explicit*
 * `false` on either era's key still means off (a player who chose it stays
 * chosen), but a key that is merely absent, because the object predates it or
 * because nobody ever touched the row, now lands on the new default instead of
 * the old one. Era 2's own key still wins outright where it is present, named
 * treatment or the retired `timeOfDay` spelling alike, exactly as before: those
 * are explicit choices too and neither reads `enhancedSky` at all.
 */
function storedEnhancedSky(raw: Partial<Record<string, unknown>>): boolean {
  const named = skyTreatmentOverride(raw["sky"]);
  // Era 2's own key wins where it is present — including the retired
  // `timeOfDay` spelling, which `skyTreatmentOverride` no longer knows.
  if (named) return named === "enhanced";
  if (raw["sky"] === "timeOfDay") return false;
  // Era 1 and era 3 read the same, which is the point. `!== false` (not
  // `=== true`) since 2026-08-26 — see the doc comment above.
  return raw["enhancedSky"] !== false;
}

/** Reads whatever survives a `JSON.parse` of untrusted stored text, falling back per-field to the default. */
export function normalizeEffectsSettings(candidate: unknown): EffectsSettings {
  if (typeof candidate !== "object" || candidate === null) return DEFAULT_EFFECTS_SETTINGS;
  const raw = candidate as Partial<Record<string, unknown>>;
  // Anything but an explicit `false` keeps the default-on behaviour, so a
  // stored object written before any key existed does not silently disable it.
  // All seven fields read this way as of 2026-08-26: `enhancedTerrain` and
  // `cockpit3d` used to read `=== true` (default *off*, so only an explicit
  // `true` turned them on) until they joined the rest of the row on by
  // default. Flipping the default without flipping the fallback would have let
  // a stored object from before the flip — where the key is simply absent —
  // keep reading as off forever; `!== false` is what makes "never touched"
  // track today's default instead of the day-it-was-added one, while a player
  // who explicitly chose off keeps reading `false` and stays off.
  return {
    propellerEffect: raw["propellerEffect"] !== false,
    killCamera: raw["killCamera"] !== false,
    enhancedEffects: raw["enhancedEffects"] !== false,
    enhancedModels: raw["enhancedModels"] !== false,
    enhancedTerrain: raw["enhancedTerrain"] !== false,
    cockpit3d: raw["cockpit3d"] !== false,
    enhancedSky: storedEnhancedSky(raw),
  };
}

export function serializeEffectsSettings(settings: EffectsSettings): string {
  return JSON.stringify(settings);
}

/** Defaults on missing key, corrupt JSON, or a non-object payload — never throws. */
export function loadEffectsSettings(store: SettingsStore): EffectsSettings {
  const raw = store.getItem(EFFECTS_SETTINGS_STORAGE_KEY);
  if (raw === null) return DEFAULT_EFFECTS_SETTINGS;
  try {
    return normalizeEffectsSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_EFFECTS_SETTINGS;
  }
}

export function saveEffectsSettings(store: SettingsStore, settings: EffectsSettings): void {
  store.setItem(EFFECTS_SETTINGS_STORAGE_KEY, serializeEffectsSettings(settings));
}
